import React, { useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import cn from 'classnames';

import { abeKpiCsvRead } from 'actions/kpi/beKpiCsv.action';
import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatDayToNameToMonth, formatDayToYearDetailed } from 'utils/date/format.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import CN from './KpiCsvDownloadButton.module.scss';

const L = logger('KpiCsvDownloadButton');

const BUTTON_NAME = 'staffKpisReports.kpiReports.downloadCsvButton';

const KpiCsvDownloadButton = ({ abeKpiCsvRead, localRanges }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isOpen, tip: toggleOpen, nay: close } = useTip(false);

  const csvResults = useAsync(async () => {
    if (!localRanges || localRanges.length === 0) return asArray();

    return Promise.all(
      localRanges.map(async ({ bgnIso, endIso }) => {
        if (!bgnIso || !endIso) return null;

        const result = await abeKpiCsvRead({
          bgn: bgnIso,
          end: endIso,
        });

        return result.text || result.json;
      }),
    );
  }, [abeKpiCsvRead, localRanges]);

  const csvData = csvResults.value;

  const generateFileName = useMemo(
    () => (bgnIso, endIso) => {
      const startDate = formatDayToNameToMonth(bgnIso);
      const endDate = formatDayToYearDetailed(endIso);

      return `${startDate} - ${endDate}.csv`;
    },
    [],
  );

  L.debug('()', 'KpiCsvDownloadButton:', csvData);

  return (
    <div
      data-bem="KpiCsvDownloadButton"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {csvData ? (
        localRanges.length === 1 ? (
          // direct download link
          <CSVLink
            data={csvData[0]}
            filename={generateFileName(localRanges[0].bgnIso, localRanges[0].endIso)}
            className={cn({
              [CN.singleDownloadButton]: true,
              [CN.disabled]: csvResults.loading,
            })}
            disabled={csvResults.loading}
          >
            <Button variant="bin" onClick={alwaysNoop}>
              <div className={CN.buttonCsvContainer}>
                <div className={CN.icon}>
                  <Icon variant="download" width="lg" />
                </div>
                {t(BUTTON_NAME)}
              </div>
            </Button>
          </CSVLink>
        ) : (
          // dropdown
          <div className={CN.dropdownWrapper}>
            <Button variant="bin" onClick={toggleOpen}>
              <div className={CN.buttonCsvContainer}>
                <div className={CN.icon}>
                  <Icon variant="download" width="lg" />
                </div>
                {t(BUTTON_NAME)}
              </div>
            </Button>
            {isOpen && (
              <DropdownFoldout width="maxInTabletP" variant="withFooter" position="bottom">
                <DropdownFoldout.Body>
                  <div data-bem="KpiCsvDownloadButton__csv-list" className={CN.scrollContainer}>
                    {csvData.map((data, index) => {
                      if (!localRanges[index]) return null; // skip invalid indexes when unselecting some quick button

                      const { bgnIso, endIso } = localRanges[index];
                      const fileName = generateFileName(bgnIso, endIso);

                      return (
                        <CSVLink
                          key={index}
                          data={data}
                          filename={fileName}
                          className={cn({
                            [CN.csvLink]: true,
                            [CN.disabled]: csvResults.loading,
                          })}
                          disabled={csvResults.loading}
                        >
                          {fileName}
                        </CSVLink>
                      );
                    })}
                  </div>
                </DropdownFoldout.Body>
                <DropdownFoldout.Footer>
                  <Button variant="outline" onClick={close}>
                    <span className={CN.buttonGap}>{t('common.buttons.closeUpp')}</span>
                  </Button>
                </DropdownFoldout.Footer>
              </DropdownFoldout>
            )}
          </div>
        )
      ) : (
        <Button variant="bin" onClick={alwaysNoop}>
          <div className={CN.buttonCsvContainer}>
            <div className={CN.icon}>
              <InlineSpinner variant="small" />
            </div>
            {t(BUTTON_NAME)}
          </div>
        </Button>
      )}
    </div>
  );
};

const KpiCsvDownloadButtonHoc = connect(
  null,
  mapActionsToDispatchToProps({
    abeKpiCsvRead,
  }),
)(KpiCsvDownloadButton);

export default KpiCsvDownloadButtonHoc;
